import type { TargetAndTransition, Transition } from 'framer-motion'
import { motion, useReducedMotion } from 'framer-motion'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import type { ThemeId } from '../../theme/ThemeContext'
import { useTheme } from '../../theme/ThemeContext'

const BOUNCE_TRAIL_LAYERS = 4
const COMMIT_FLY_HOLD_MS = 320
/** 相对原半宽放大水平摆动，使小球活动区更宽（父级已 overflow-visible） */
const PULL_TRAVEL_X_AMP = 1.32
const ORB_CELL = 'h-[3.75rem] w-[3.75rem]'

function themeUsesOrbRing(theme: ThemeId): boolean {
  return theme === 'cool' || theme === 'dark' || theme === 'glass'
}

/** 无底部横条进度：圆环主题 + 暖色（仅弧点） */
function themeCompactPullStrip(theme: ThemeId): boolean {
  return themeUsesOrbRing(theme) || theme === 'warm'
}

/** 像素风：水平阶梯关键帧（去程 + 回程） */
function pixelSteppedX(travel: number, steps: number): number[] {
  const t = Math.max(travel, 0)
  if (t <= 0) return [0]
  const up: number[] = []
  for (let i = 0; i <= steps; i++) up.push((t * i) / steps)
  const down: number[] = []
  for (let i = steps - 1; i >= 0; i--) down.push((t * i) / steps)
  return [0, ...up.slice(1), ...down.slice(0, -1), 0]
}

type BounceMotion = {
  animate: TargetAndTransition
  transition: Transition
}

/** 五帧：水平 0 → +x → 0 → −x → 0（关于竖直 y 轴左右对称） */
function sym5<T extends number>(zero: T, pos: T, neg: T): [T, T, T, T, T] {
  return [zero, pos, zero, neg, zero]
}

/**
 * 竖直 y 轴镜像轨迹：在 x=±a 两端的 y 必须相同，点 (a,y) 与 (−a,y) 对称。
 */
function sym5YEven<T extends number>(zero: T, yAtXExtrema: T): [T, T, T, T, T] {
  return [zero, yAtXExtrema, zero, yAtXExtrema, zero]
}

const T5 = [0, 0.25, 0.5, 0.75, 1] as const

function themeBounceMotion(theme: ThemeId, travel: number, layerIndex: number): BounceMotion {
  const x = Math.max(travel, 0)
  const Y = 1.22

  switch (theme) {
    case 'warm': {
      const stagger = 0.11
      return {
        animate: {
          x: sym5(0, x * 0.26, -x * 0.26),
          y: sym5YEven(0, -8.5 * Y),
        },
        transition: {
          duration: 2.08,
          repeat: Infinity,
          times: [...T5],
          ease: [0.4, 0, 0.35, 1],
          delay: layerIndex * stagger,
        },
      }
    }
    case 'cool': {
      const stagger = 0.07
      return {
        animate: {
          x: sym5(0, x, -x),
          y: sym5YEven(0, 2.2 * Y),
          scale: sym5(1, 1.06, 1.06),
        },
        transition: {
          duration: 1.12,
          repeat: Infinity,
          times: [0, 0.18, 0.42, 0.68, 1],
          ease: [0.25, 0.9, 0.35, 1],
          delay: layerIndex * stagger,
        },
      }
    }
    case 'dark': {
      const stagger = 0.14
      return {
        animate: {
          x: sym5(0, x * 0.9, -x * 0.9),
          y: sym5YEven(0, -4.2 * Y),
        },
        transition: {
          duration: 2.62,
          repeat: Infinity,
          times: [0, 0.32, 0.5, 0.68, 1],
          ease: [0.52, 0, 0.18, 1],
          delay: layerIndex * stagger,
        },
      }
    }
    case 'glass': {
      const stagger = 0.06
      const du = 1.06
      return {
        animate: {
          x: sym5(0, x * 0.72, -x * 0.72),
          y: sym5YEven(0, -5.8 * Y),
          rotate: sym5(0, 7.5, -7.5),
          scaleX: sym5(1, 1.06, 1.06),
          skewY: sym5YEven(0, 2.8),
        },
        transition: {
          duration: du,
          repeat: Infinity,
          times: [...T5],
          ease: 'easeInOut',
          delay: layerIndex * stagger,
        },
      }
    }
    case 'cartoon': {
      const stagger = 0.085
      const xp = x * 1.04
      return {
        animate: {
          x: sym5(0, xp, -xp),
          y: sym5YEven(0, -9.5 * Y),
          scaleY: sym5(1, 0.88, 0.88),
        },
        transition: {
          duration: 0.92,
          repeat: Infinity,
          times: [0, 0.14, 0.5, 0.86, 1],
          ease: [0.22, 1.45, 0.28, 1],
          delay: layerIndex * stagger,
        },
      }
    }
    case 'illustration': {
      const stagger = 0.1
      const xp = x * 0.58
      return {
        animate: {
          x: sym5(0, xp, -xp),
          y: sym5YEven(0, 5.5 * Y),
          rotate: sym5(0, 5.2, -5.2),
        },
        transition: {
          duration: 1.88,
          repeat: Infinity,
          times: [0, 0.26, 0.5, 0.74, 1],
          ease: [0.42, 0, 0.58, 1],
          delay: layerIndex * stagger,
        },
      }
    }
    case 'pixel': {
      const stagger = 0.05
      const xsRaw = pixelSteppedX(x, 5)
      const xm = Math.max(...xsRaw, 0) / 2
      const xs = xsRaw.map((v) => v - xm)
      const ys = xs.map(() => 0)
      return {
        animate: { x: xs, y: ys },
        transition: {
          duration: 0.74,
          repeat: Infinity,
          ease: 'linear',
          delay: layerIndex * stagger,
        },
      }
    }
    case 'geek': {
      const stagger = 0.045
      return {
        animate: {
          x: sym5(0, x * 0.98, -x * 0.98),
          y: sym5YEven(0, -1.1 * Y),
          skewX: sym5(0, 0.55, -0.55),
        },
        transition: {
          duration: 0.86,
          repeat: Infinity,
          times: [0, 0.1, 0.5, 0.9, 1],
          ease: [0.4, 0, 0.2, 1],
          delay: layerIndex * stagger,
        },
      }
    }
    default: {
      const stagger = 0.085
      return {
        animate: { x: sym5(0, x * 0.82, -x * 0.82) },
        transition: {
          duration: 1.36,
          repeat: Infinity,
          times: [0, 0.24, 0.5, 0.76, 1],
          ease: 'easeInOut',
          delay: layerIndex * stagger,
        },
      }
    }
  }
}

function themePullScaleTransition(theme: ThemeId, reduced: boolean): Transition {
  if (reduced) return { duration: 0 }
  switch (theme) {
    case 'warm':
      return { duration: 0.16, ease: [0.45, 0.05, 0.55, 0.95] }
    case 'cool':
      return { duration: 0.2, ease: [0.33, 1, 0.68, 1] }
    case 'dark':
      return { duration: 0.22, ease: [0.4, 0, 0.6, 1] }
    case 'glass':
      return { duration: 0.24, ease: [0.22, 1, 0.36, 1] }
    case 'cartoon':
      return { duration: 0.17, ease: [0.34, 1.45, 0.64, 1] }
    case 'illustration':
      return { duration: 0.21, ease: [0.42, 0, 0.58, 1] }
    case 'pixel':
      return { duration: 0.06, ease: 'linear' }
    case 'geek':
      return { duration: 0.07, ease: 'linear' }
    default:
      return { duration: 0.14, ease: [0.22, 1, 0.36, 1] }
  }
}

type PullDiverseProps = {
  theme: ThemeId
  progress: number
  angular: boolean
  arcOpacity: number
  transition: Transition
}

const pullTrackBorder = 'border border-[color-mix(in_srgb,var(--accent)_16%,transparent)] bg-transparent'

/** 整圈轨道：i=0 在正上，顺时针 */
function orbitXY(i: number, n: number, r: number): { x: number; y: number } {
  const a = (i / n) * Math.PI * 2
  return { x: Math.sin(a) * r, y: -Math.cos(a) * r }
}

/** 进度块从中间向两侧依次点亮（用于像素条、极客字符等） */
function centerOutIndices(n: number): number[] {
  if (n <= 0) return []
  const mid = (n - 1) / 2
  const out: number[] = []
  for (let d = 0; d < n; d++) {
    const lo = Math.floor(mid - d)
    const hi = Math.ceil(mid + d)
    if (lo === hi) {
      if (lo >= 0 && lo < n) out.push(lo)
    } else {
      if (lo >= 0 && lo < n) out.push(lo)
      if (hi >= 0 && hi < n && hi !== lo) out.push(hi)
    }
  }
  return out
}

type PullOrbChromeProps = {
  theme: ThemeId
  progress: number
  arcOpacity: number
  transition: Transition
}

type PullOrbRingProps = {
  theme: ThemeId
  progress: number
  arcOpacity: number
  transition: Transition
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v))
}

/** 云蓝：上半圆弧（天际线），非闭合圆环 */
function PullOrbCoolProgress({
  progress,
  arcOpacity,
  transition,
}: Omit<PullOrbRingProps, 'theme'>) {
  const r = 24.5
  const cx = 32
  const cy = 32
  const arcPath = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`
  const len = Math.PI * r
  const off = len * (1 - clamp01(progress))

  return (
    <motion.svg
      className="pointer-events-none absolute inset-0 z-0 h-full w-full text-accent"
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden
      style={{ opacity: arcOpacity }}
      initial={false}
    >
      <path
        d={arcPath}
        stroke="currentColor"
        strokeOpacity={0.13}
        strokeWidth={2.9}
        strokeLinecap="round"
      />
      <motion.path
        d={arcPath}
        stroke="currentColor"
        strokeOpacity={0.82}
        strokeWidth={2.9}
        strokeLinecap="round"
        strokeDasharray={len}
        initial={false}
        animate={{ strokeDashoffset: off }}
        transition={transition}
      />
    </motion.svg>
  )
}

/** 暗夜：左右对称竖条，从中间向上下生长（非环形） */
function PullOrbDarkProgress({
  progress,
  arcOpacity,
  transition,
}: Omit<PullOrbRingProps, 'theme'>) {
  const p = clamp01(progress)
  const rail =
    'rounded-full bg-[color-mix(in_srgb,var(--accent)_18%,transparent)] ring-1 ring-[color-mix(in_srgb,var(--accent)_28%,transparent)]'

  return (
    <div
      className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center"
      style={{ opacity: arcOpacity }}
      aria-hidden
    >
      <div className="relative h-[46px] w-[52px]">
        <div className={`absolute left-0 top-1/2 h-[42px] w-[3px] -translate-y-1/2 overflow-hidden ${rail}`}>
          <motion.div
            className="h-full w-full origin-center rounded-full bg-accent"
            initial={false}
            animate={{ scaleY: p }}
            transition={transition}
          />
        </div>
        <div className={`absolute right-0 top-1/2 h-[42px] w-[3px] -translate-y-1/2 overflow-hidden ${rail}`}>
          <motion.div
            className="h-full w-full origin-center rounded-full bg-accent"
            initial={false}
            animate={{ scaleY: p }}
            transition={transition}
          />
        </div>
      </div>
    </div>
  )
}

/** 玻璃：水平扁椭圆；下拉时左侧弧与右侧弧自底朝上同时绘制（各占半周近似长度） */
function PullOrbGlassProgress({
  progress,
  arcOpacity,
  transition,
}: Omit<PullOrbRingProps, 'theme'>) {
  const cx = 32
  const cy = 32
  /** 长轴水平：时间线下拉语义为扁椭圆镜片 */
  const rx = 27.5
  const ry = 18.5
  const perimeter =
    Math.PI * (3 * (rx + ry) - Math.sqrt((3 * rx + ry) * (rx + 3 * ry)))
  /** Ramanujan 近似闭合周长；单侧弧取一半作为 dash 标尺 */
  const halfLen = perimeter / 2
  const p = clamp01(progress)
  const off = halfLen * (1 - p)
  /** 自底顶点 (θ=π/2) → 顶顶点 (θ=-π/2)：左/右各走半椭圆，sweep 取反向以沿左右两侧上移 */
  const leftArcPath = `M ${cx} ${cy + ry} A ${rx} ${ry} 0 1 1 ${cx} ${cy - ry}`
  const rightArcPath = `M ${cx} ${cy + ry} A ${rx} ${ry} 0 1 0 ${cx} ${cy - ry}`

  return (
    <motion.svg
      className="pointer-events-none absolute inset-0 z-0 h-full w-full text-accent"
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden
      style={{ opacity: arcOpacity }}
      initial={false}
      animate={{ y: [0, -1.85, 0] }}
      transition={{ duration: 1.45, repeat: Infinity, ease: 'easeInOut' }}
    >
      <ellipse
        cx={cx}
        cy={cy}
        rx={rx}
        ry={ry}
        stroke="currentColor"
        strokeOpacity={0.2}
        strokeWidth={2}
        strokeDasharray="3 5"
      />
      <motion.path
        d={leftArcPath}
        stroke="currentColor"
        strokeOpacity={0.78}
        strokeWidth={2.15}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        strokeDasharray={halfLen}
        initial={false}
        animate={{ strokeDashoffset: off }}
        transition={transition}
      />
      <motion.path
        d={rightArcPath}
        stroke="currentColor"
        strokeOpacity={0.78}
        strokeWidth={2.15}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        strokeDasharray={halfLen}
        initial={false}
        animate={{ strokeDashoffset: off }}
        transition={transition}
      />
    </motion.svg>
  )
}

/** 云蓝 / 暗夜 / 玻璃：三种不同形态的紧凑读数（非统一圆环） */
function PullOrbRingProgress(props: PullOrbRingProps) {
  const { theme, progress, arcOpacity, transition } = props
  const rest = { progress, arcOpacity, transition }
  if (theme === 'cool') return <PullOrbCoolProgress {...rest} />
  if (theme === 'dark') return <PullOrbDarkProgress {...rest} />
  return <PullOrbGlassProgress {...rest} />
}

/** 仅部分主题：小球近旁的「局部/整圈」点阵（与下方线条/块并存或单独承担读数） */
function PullOrbLocalChrome({ theme, progress, arcOpacity, transition }: PullOrbChromeProps) {
  const lit = (n: number) => Math.min(n, Math.floor(progress * n + 1e-6))
  const dot = (key: string, x: number, y: number, on: boolean, cls: string, s: number) => (
    <motion.div
      key={key}
      className={cls}
      initial={false}
      animate={{ opacity: on ? 1 : 0.2, scale: on ? 1.08 : 0.88 }}
      transition={transition}
      style={{
        position: 'absolute',
        left: `calc(50% + ${x}px - ${s / 2}px)`,
        top: `calc(50% + ${y}px - ${s / 2}px)`,
        width: s,
        height: s,
      }}
    />
  )

  const shell = (children: ReactNode) => (
    <div className="pointer-events-none absolute inset-0 z-0" style={{ opacity: arcOpacity }} aria-hidden>
      {children}
    </div>
  )

  switch (theme) {
    case 'warm': {
      const r = 31
      const angles = [-0.85, -0.5, -0.15].map((t) => -Math.PI / 2 + Math.PI * t)
      return shell(
        <>
          {angles.map((a, i) => {
            const on = i < lit(3)
            return dot(
              `wa${i}`,
              Math.sin(a) * r,
              -Math.cos(a) * r,
              on,
              'rounded-full bg-[color-mix(in_srgb,var(--accent)_52%,var(--surface)_35%)]',
              4,
            )
          })}
        </>,
      )
    }
    case 'illustration':
      return shell(
        <>
          {dot(
            'il0',
            -34,
            2,
            progress > 0.12,
            'rounded-full bg-[color-mix(in_srgb,var(--accent)_42%,transparent)]',
            5,
          )}
          {dot(
            'il1',
            34,
            -2,
            progress > 0.45,
            'rounded-full bg-[color-mix(in_srgb,var(--accent)_42%,transparent)]',
            4,
          )}
        </>,
      )
    case 'pixel': {
      const n = 4
      const L = lit(n)
      return shell(
        <>
          {Array.from({ length: n }, (_, i) => {
            const { x, y } = orbitXY(i, n, 26)
            const on = i < L
            return dot(
              `px${i}`,
              x,
              y,
              on,
              `border border-[color-mix(in_srgb,var(--accent)_42%,transparent)] ${on ? 'bg-accent/90' : 'bg-transparent'}`,
              4,
            )
          })}
        </>,
      )
    }
    default:
      return null
  }
}

/**
 * 下方主读数：线条 / 块 / 翻转等（圆环主题不调用此组件）。
 */
function PullThemeProgressDiverse({
  theme,
  progress,
  angular,
  arcOpacity,
  transition,
}: PullDiverseProps) {
  const reduced = Boolean(useReducedMotion())
  const wrapRound = angular ? 'rounded-md' : 'rounded-full'
  const capL = angular ? 'rounded-l-md' : 'rounded-l-full'
  const capR = angular ? 'rounded-r-md' : 'rounded-r-full'

  const row = (node: ReactNode) => (
    <div
      className="pointer-events-none w-full max-w-[min(20rem,calc(100vw-2rem))] px-0.5"
      style={{ opacity: arcOpacity }}
      aria-hidden
    >
      {node}
    </div>
  )

  /** 双侧填充：从轨道中线向左右两侧对称延伸 */
  const dualSide = (fillClass: string, h: string) =>
    row(
      <div className={`relative ${h} w-full overflow-hidden ${pullTrackBorder} ${wrapRound}`}>
        <div className="absolute inset-y-0 left-0 w-1/2 overflow-hidden">
          <motion.div
            className={`h-full w-full origin-right ${fillClass} ${capL}`}
            initial={false}
            animate={{ scaleX: progress }}
            transition={transition}
          />
        </div>
        <div className="absolute inset-y-0 right-0 w-1/2 overflow-hidden">
          <motion.div
            className={`h-full w-full origin-left ${fillClass} ${capR}`}
            initial={false}
            animate={{ scaleX: progress }}
            transition={transition}
          />
        </div>
      </div>,
    )

  switch (theme) {
    case 'warm':
      return null
    case 'cartoon': {
      const outerR = angular ? 'rounded-xl' : 'rounded-full'
      return row(
        <motion.div
          className="w-full"
          initial={false}
          animate={{ rotateX: (1 - progress) * -22 }}
          transition={transition}
          style={{ transformOrigin: '50% 50%' }}
        >
          <div
            className={`relative h-2 w-full overflow-hidden border-2 border-[color-mix(in_srgb,var(--ink)_22%,transparent)] bg-transparent ${outerR}`}
          >
            <div className="absolute inset-y-0 left-0 w-1/2 overflow-hidden">
              <motion.div
                className={`h-full w-full origin-right bg-accent shadow-[2px_0_0_0_color-mix(in_srgb,var(--ink)_26%,transparent)] ${angular ? 'rounded-l-xl' : 'rounded-l-full'}`}
                initial={false}
                animate={{ scaleX: progress }}
                transition={transition}
              />
            </div>
            <div className="absolute inset-y-0 right-0 w-1/2 overflow-hidden">
              <motion.div
                className={`h-full w-full origin-left bg-accent shadow-[-2px_0_0_0_color-mix(in_srgb,var(--ink)_26%,transparent)] ${angular ? 'rounded-r-xl' : 'rounded-r-full'}`}
                initial={false}
                animate={{ scaleX: progress }}
                transition={transition}
              />
            </div>
          </div>
        </motion.div>,
      )
    }
    case 'illustration':
      return row(
        <motion.div
          className="relative h-1.5 w-full"
          initial={false}
          animate={{ rotateZ: (1 - progress) * -5 }}
          transition={transition}
          style={{ transformOrigin: 'center center' }}
        >
          <div
            className="absolute inset-0 -skew-x-[9deg] rounded-full bg-[color-mix(in_srgb,var(--accent)_8%,transparent)]"
            aria-hidden
          />
          <motion.div
            className="absolute inset-0 origin-center -skew-x-[9deg] rounded-full bg-gradient-to-r from-transparent via-[color-mix(in_srgb,var(--accent)_38%,transparent)] to-transparent"
            initial={false}
            animate={{ scaleX: Math.max(0.06, progress) }}
            transition={transition}
          />
        </motion.div>,
      )
    case 'pixel': {
      const n = 8
      const k = Math.min(n, Math.ceil(progress * n - 1e-6))
      const order = centerOutIndices(n)
      const lit = new Set(order.slice(0, k))
      const pxR = angular ? 'rounded-sm' : 'rounded-none'
      return row(
        <motion.div
          className={`flex h-1.5 w-full gap-px ${pullTrackBorder} p-px ${pxR}`}
          initial={false}
          animate={{ rotateX: (1 - progress) * 62 }}
          transition={transition}
          style={{ transformOrigin: 'center center' }}
        >
          {Array.from({ length: n }, (_, i) => (
            <div
              key={i}
              className={[
                'min-w-0 flex-1',
                lit.has(i) ? 'bg-accent' : 'bg-transparent',
                angular ? '' : 'rounded-none',
              ].join(' ')}
            />
          ))}
        </motion.div>,
      )
    }
    case 'geek': {
      const segs = 16
      const k = Math.min(segs, Math.floor(progress * segs + 1e-9))
      const hexByte = Math.round(Math.min(1, progress) * 255)
        .toString(16)
        .toUpperCase()
        .padStart(2, '0')
      const pct = Math.round(Math.min(1, progress) * 100)
        .toString()
        .padStart(3, '0')
      return row(
        <motion.div
          className="flex w-full min-w-0 items-center gap-1.5 px-1 py-0.5 font-mono text-[9px] leading-none tracking-tight text-[color-mix(in_srgb,var(--accent)_68%,var(--ink)_22%)] sm:gap-2 sm:px-2 sm:text-[10px]"
          initial={false}
          animate={{
            skewX: reduced ? 0 : (1 - progress) * 1.8,
            boxShadow: reduced
              ? '0 0 0 0 transparent'
              : [
                  '0 0 0 0 transparent',
                  '0 0 12px color-mix(in_srgb,var(--accent)_12%,transparent)',
                  '0 0 0 0 transparent',
                ],
          }}
          transition={
            reduced
              ? transition
              : {
                  skewX: transition,
                  boxShadow: { duration: 2.2, repeat: Infinity, ease: 'easeInOut' },
                }
          }
        >
          <span className="shrink-0 select-none opacity-75" aria-hidden>
            &gt;stream
          </span>
          <span
            className="flex min-h-[1em] min-w-0 flex-1 items-stretch font-mono"
            aria-hidden
          >
            {Array.from({ length: segs }, (_, i) => (
              <span
                key={i}
                className={[
                  'flex min-w-0 flex-1 basis-0 items-center justify-center overflow-hidden',
                  i < k
                    ? 'text-accent'
                    : 'text-[color-mix(in_srgb,var(--accent)_22%,transparent)]',
                ].join(' ')}
              >
                {i < k ? '█' : '░'}
              </span>
            ))}
          </span>
          <span className="flex shrink-0 items-baseline gap-1.5 tabular-nums sm:gap-2" aria-hidden>
            <span className="opacity-85">
              0x{hexByte}
            </span>
            <span className="opacity-60">{pct}%</span>
            <motion.span
              className="inline-block w-[0.55em] text-accent"
              aria-hidden
              animate={reduced ? { opacity: 1 } : { opacity: [1, 0.15, 1] }}
              transition={
                reduced ? { duration: 0 } : { duration: 0.72, repeat: Infinity, ease: 'linear' }
              }
            >
              ▍
            </motion.span>
          </span>
        </motion.div>,
      )
    }
    default:
      return dualSide('bg-[color-mix(in_srgb,var(--accent)_48%,transparent)]', 'h-1.5')
  }
}

/** 用于计算水平行程：取主题块最大宽度 */
function themeGemTravelWidth(theme: ThemeId): number {
  switch (theme) {
    case 'pixel':
      return 20
    case 'dark':
      return 20
    case 'geek':
      return 24
    default:
      return 22
  }
}

const gemGlow =
  'bg-accent shadow-[0_0_10px_color-mix(in_srgb,var(--accent)_48%,transparent)]'

const gemPull = 'bg-accent'

/** 八个主题各一种实心小形，与设置页气质对齐；下拉时用 forPull 去掉大范围 shadow，避免浅色方底 */
function ThemeRefreshGem({ theme, forPull }: { theme: ThemeId; forPull?: boolean }) {
  const g = forPull ? gemPull : gemGlow
  switch (theme) {
    case 'warm':
      return <div className={`h-[20px] w-[20px] rounded-[8px] ${g}`} aria-hidden />
    case 'cool':
      return <div className={`h-[20px] w-[20px] rounded-full ${g}`} aria-hidden />
    case 'dark':
      return <div className={`h-[18px] w-[18px] rounded-full ${g}`} aria-hidden />
    case 'glass':
      return (
        <div
          className={[
            'h-[20px] w-[20px] rounded-full',
            g,
            forPull
              ? 'ring-2 ring-[color-mix(in_srgb,var(--accent)_38%,transparent)]'
              : 'ring-2 ring-[color-mix(in_srgb,var(--accent)_40%,transparent)]',
          ].join(' ')}
          aria-hidden
        />
      )
    case 'cartoon':
      return (
        <div
          className={[
            'h-[22px] w-[22px] rounded-2xl',
            g,
            forPull
              ? 'ring-2 ring-[color-mix(in_srgb,var(--ink)_22%,transparent)]'
              : 'shadow-[3px_3px_0_0_color-mix(in_srgb,var(--ink)_32%,transparent)]',
          ].join(' ')}
          aria-hidden
        />
      )
    case 'illustration':
      return (
        <div
          className={`h-[22px] w-[22px] ${g}`}
          style={{ borderRadius: '58% 42% 55% 45% / 45% 55% 48% 52%' }}
          aria-hidden
        />
      )
    case 'pixel':
      return (
        <div
          className={[
            'h-[18px] w-[18px] rounded-none',
            g,
            forPull ? '' : 'shadow-[2px_2px_0_0_color-mix(in_srgb,var(--ink)_40%,transparent)]',
          ].join(' ')}
          aria-hidden
        />
      )
    case 'geek':
      return (
        <div
          className={[
            'h-2 w-6 rounded-none ring-1 ring-inset ring-[color-mix(in_srgb,var(--accent)_42%,transparent)]',
            g,
            forPull ? 'shadow-[inset_0_1px_0_0_color-mix(in_srgb,var(--surface)_40%,transparent)]' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-hidden
        />
      )
    default:
      return <div className={`h-[20px] w-[20px] rounded-full ${g}`} aria-hidden />
  }
}

type PullIndicatorProps = {
  pullPx: number
  releaseThreshold: number
  angular: boolean
  commitFly: boolean
  onCommitFlyComplete?: () => void
  refreshing?: boolean
}

export function TimelinePullIndicator({
  pullPx,
  releaseThreshold,
  angular,
  commitFly,
  onCommitFlyComplete,
  refreshing = false,
}: PullIndicatorProps) {
  const { theme } = useTheme()
  const gemW = themeGemTravelWidth(theme)
  const reduced = useReducedMotion()
  const progress = Math.min(1, pullPx / releaseThreshold)
  const trackRound = angular ? 'rounded-lg' : 'rounded-full'
  const arcOpacity = progress < 0.04 ? 0 : 1
  const usesOrbRing = themeUsesOrbRing(theme)
  const compactStrip = themeCompactPullStrip(theme)
  const stripH = compactStrip ? 'h-[4.25rem] min-h-[4.25rem]' : 'h-20 min-h-20'
  const trackMinH = compactStrip ? 'min-h-[4.25rem]' : 'min-h-20'

  const completeRef = useRef(onCommitFlyComplete)
  completeRef.current = onCommitFlyComplete
  const flyDoneRef = useRef(false)

  const trackInnerRef = useRef<HTMLDivElement | null>(null)
  const [travelX, setTravelX] = useState(0)

  useLayoutEffect(() => {
    const el = trackInnerRef.current
    if (!el) return
    const measure = () => {
      const w = el.clientWidth
      const raw = Math.max(0, w - gemW)
      const ideal = (raw / 2) * PULL_TRAVEL_X_AMP
      /** 水平摆动不得超出轨道，避免父级 overflow 裁切后只剩「半边」动画 */
      const maxTravel = Math.max(0, w / 2 - gemW / 2 - 4)
      const half = Math.min(ideal, maxTravel)
      setTravelX(theme === 'cartoon' ? half / 1.06 : half)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [gemW, theme])

  const fireCompleteOnce = useCallback(() => {
    if (flyDoneRef.current) return
    flyDoneRef.current = true
    completeRef.current?.()
  }, [])

  const bouncing = refreshing || commitFly
  const showBounce = bouncing && !reduced
  const showBounceReduced = bouncing && !!reduced

  useEffect(() => {
    if (!commitFly) {
      flyDoneRef.current = false
      return
    }
    const id = window.setTimeout(fireCompleteOnce, COMMIT_FLY_HOLD_MS)
    return () => window.clearTimeout(id)
  }, [commitFly, fireCompleteOnce])

  return (
    <div
      className={[
        'mb-2 flex w-full max-w-full justify-center overflow-visible px-2 sm:mb-3',
        stripH,
      ].join(' ')}
    >
      <div
        className={[
          'relative flex w-full max-w-[min(20rem,calc(100vw-2rem))] items-center overflow-visible',
          stripH,
        ].join(' ')}
      >
        <div
          aria-hidden
          className={[
            'pointer-events-none absolute inset-0 z-0 overflow-visible',
            'bg-transparent shadow-none',
            trackRound,
          ].join(' ')}
        />
        <div
          role="status"
          aria-live="polite"
          aria-busy={bouncing ? true : undefined}
          className={['relative z-[1] w-full overflow-visible pointer-events-none', stripH].join(' ')}
        >
          <div
            ref={trackInnerRef}
            className={[
              'absolute left-2 right-2 top-1/2 -translate-y-1/2 overflow-visible',
              trackMinH,
            ].join(' ')}
          >
            {showBounce
              ? Array.from({ length: BOUNCE_TRAIL_LAYERS }, (_, i) => {
                  const { animate, transition } = themeBounceMotion(
                    theme,
                    Math.max(travelX, 0),
                    i,
                  )
                  return (
                    <div
                      key={i}
                      className="pointer-events-none absolute left-1/2 top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2"
                      style={{
                        opacity: 1 - i * 0.24,
                        zIndex: BOUNCE_TRAIL_LAYERS - i,
                      }}
                    >
                      <motion.div
                        className="flex origin-center will-change-transform items-center justify-center"
                        animate={animate}
                        transition={transition}
                      >
                        <ThemeRefreshGem theme={theme} />
                      </motion.div>
                    </div>
                  )
                })
              : null}

            {showBounceReduced ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <ThemeRefreshGem theme={theme} />
              </div>
            ) : null}

            {!bouncing ? (
              <div
                className={[
                  'absolute inset-0 flex flex-col items-center justify-center overflow-visible px-0.5 pt-1 pb-3 sm:pb-3.5',
                  compactStrip ? 'gap-0' : 'gap-0.5',
                ].join(' ')}
              >
                <div className={['relative grid shrink-0 place-items-center', ORB_CELL].join(' ')}>
                  {usesOrbRing ? (
                    <PullOrbRingProgress
                      theme={theme}
                      progress={progress}
                      arcOpacity={arcOpacity}
                      transition={themePullScaleTransition(theme, Boolean(reduced))}
                    />
                  ) : null}
                  <PullOrbLocalChrome
                    theme={theme}
                    progress={progress}
                    arcOpacity={arcOpacity}
                    transition={themePullScaleTransition(theme, Boolean(reduced))}
                  />
                  <motion.div
                    className="relative z-[1] inline-flex items-center justify-center bg-transparent will-change-transform"
                    animate={{
                      scale: 0.52 + 0.48 * progress,
                      opacity: arcOpacity,
                    }}
                    transition={themePullScaleTransition(theme, Boolean(reduced))}
                  >
                    <ThemeRefreshGem theme={theme} forPull />
                  </motion.div>
                </div>
                {!compactStrip ? (
                  <PullThemeProgressDiverse
                    theme={theme}
                    progress={progress}
                    angular={angular}
                    arcOpacity={arcOpacity}
                    transition={themePullScaleTransition(theme, Boolean(reduced))}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
